// music/primitives.jsx — layout primitives shared by S0, S1, S2.
//
// 2026-10 design: T roles only, A_FONT only, no outlines (a hairline
// only as a list divider), ALabel eyebrows, Inter Tight for names and
// controls, Cormorant only for large titles. No new colour primitives.
//
// PR 2 round A introduces Section, Carousel, ContentTile. Round B
// adds FavouriteTile + SourceTile. Round C adds SearchInput. Round D
// adds TrackRow.

import * as React from 'react';
import { CoverArt, I, Slider } from '../shared.jsx';
import { A_FONT, ALabel, useImageBlobUrl, fs } from '../direction-a.jsx';
import { RADIUS } from '../theme.js';
import { shouldSend, isSettled, SEND_INTERVAL_MS, SETTLE_TIMEOUT_MS } from './volume-drag.js';

// Section — ALabel eyebrow header + content slot. Optional `onSeeAll`
// tap target in the top-right; rendered as a quiet text affordance,
// not a chip, to keep visual hierarchy with the eyebrow. Optional
// `icon` renders left of the eyebrow text (ALabel draws it at 12px in
// T.mute) — used by S1 search sections to glyph the content type
// (track / album / playlist / artist / show / episode).
export function Section({ T, eyebrow, icon, children, onSeeAll, style }) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 12, ...style }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 20 }}>
        <ALabel T={T} icon={icon}>{eyebrow}</ALabel>
        {onSeeAll && (
          <button onClick={onSeeAll} style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            minHeight: 44, padding: '0 4px', margin: '-12px 0',
            fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500, color: T.inkSoft,
          }}>
            See all
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

// Carousel — horizontal scroll with scroll-snap. Children render as
// snap targets. We don't fight the native scrollbar — at the panel's
// main 1024px form factor scroll-snap + mouse-wheel + drag work fine
// out of the box.
export function Carousel({ children, gap = 12 }) {
  return (
    <div style={{
      display: 'flex', gap, overflowX: 'auto',
      scrollSnapType: 'x mandatory',
      paddingBottom: 4,
      scrollbarWidth: 'thin',
      WebkitOverflowScrolling: 'touch',
    }}>
      {React.Children.map(children, (child) => (
        <div style={{ scrollSnapAlign: 'start', flexShrink: 0 }}>{child}</div>
      ))}
    </div>
  );
}

// SourceTile — plain card square + vinyl glyph instead of artwork.
// Visually distinct from ContentTile so the user doesn't expect
// immediate dispatch — tap opens the line-in picker overlay (PR 5; PR 2
// stubs to a placeholder callback).
export function SourceTile({ T, label, onClick, size = 120 }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', flexDirection: 'column', gap: 8,
      width: size, padding: 0, border: 'none', background: 'transparent',
      cursor: 'pointer', textAlign: 'left',
      fontFamily: A_FONT.sans, color: T.ink,
    }}>
      <div style={{
        width: size, height: size, borderRadius: RADIUS.key,
        background: T.card, boxShadow: T.cardShadow,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: T.inkSoft,
      }}>
        {/* Vinyl glyph — concentric circles + a needle tick. */}
        <svg width={size * 0.42} height={size * 0.42} viewBox="0 0 24 24"
             fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9"/>
          <circle cx="12" cy="12" r="4"/>
          <circle cx="12" cy="12" r="1.3" fill="currentColor"/>
          <path d="M19 5l2-2"/>
        </svg>
      </div>
      <div style={{ minWidth: 0, width: '100%' }}>
        <div style={{
          fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
          color: T.ink, lineHeight: 1.25,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{label}</div>
        <div style={{
          fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.inkSoft,
          marginTop: 2,
        }}>Source</div>
      </div>
    </button>
  );
}

// SourceCover — cover-art treatment for the no-metadata case (line-in
// today, radio without ID3/ICY later). Quiet-fill background + large
// centered glyph in the secondary ink. Per BRIEF-music-card.md §11.13 answer: visually
// distinct from the procedural CoverArt fallback so the user reads
// "this is a line-in source" at a glance without needing to scan the
// title. Replaces the gradient for any source the music card can't
// render album art for.
//
// `kind` drives the glyph. 'vinyl' — turntable (Office Turntable).
// 'line-in' — generic 3.5mm aux jack (Pavilion / Rumpus / Pool / Master
// Bed / Ensuite, or any other Sonos with Line-in active). 'radio' is
// reserved for the metadata-less radio variant (PR 7).
export function SourceCover({ T, kind = 'line-in', size = 180, radius = 12 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: radius,
      background: T.cardSoft,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: T.inkSoft,
      position: 'relative',
    }}>
      {kind === 'vinyl' && (
        <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 24 24"
             fill="none" stroke="currentColor" strokeWidth="1.2"
             strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <circle cx="12" cy="12" r="6.5"/>
          <circle cx="12" cy="12" r="3.5"/>
          <circle cx="12" cy="12" r="1.2" fill="currentColor"/>
        </svg>
      )}
      {kind === 'line-in' && (
        // 3.5mm "aux" jack profile — plug + cable. Three contact rings
        // on the pin (TRS stereo); reads as "audio jack" at a glance.
        <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 24 24"
             fill="none" stroke="currentColor" strokeWidth="1.3"
             strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2v9"/>
          <path d="M9 11h6v3a3 3 0 01-3 3 3 3 0 01-3-3v-3z"/>
          <path d="M12 17v5"/>
          <path d="M10 5h4"/>
          <path d="M10 7.5h4"/>
        </svg>
      )}
      {kind === 'radio' && (
        <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 24 24"
             fill="none" stroke="currentColor" strokeWidth="1.3"
             strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 16.5V9a2 2 0 012-2h12a2 2 0 012 2v7.5"/>
          <circle cx="16" cy="13" r="2"/>
          <path d="M7 11h5M7 14h3"/>
          <path d="M8 7l8-3"/>
        </svg>
      )}
    </div>
  );
}

// SearchInput — sticky search row used inside S1. Stateless / controlled
// (caller owns the value + onChange + onClear). Mic affordance is
// stubbed for PR 2 — HA Assist binding lands in a polish PR.
//
// The `data-music-search` attribute is the hook MusicShell's window-
// level keydown blocker uses to identify our input and swallow
// HA Frontend's document-level keyboard shortcuts (a=Assist, c=command
// palette, e=edit, m=menu) when focus is here. Without that blocker
// typing "Olivia Dean" would trip Assist on the 'a'. See MusicShell
// in shell.jsx for the blocker.
export function SearchInput({ T, value, onChange, onClear, autoFocus = true, placeholder = 'Search Spotify' }) {
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 1,
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '4px 4px 4px 18px', minHeight: 52,
      background: T.card, borderRadius: RADIUS.control, boxShadow: T.cardShadow,
    }}>
      <span style={{ color: T.inkSoft, display: 'flex', flexShrink: 0, marginRight: 4 }}>{I.search}</span>
      <input
        data-music-search="1"
        type="text" value={value} onChange={(e) => onChange(e.target.value)}
        autoFocus={autoFocus} placeholder={placeholder}
        style={{
          flex: 1, border: 'none', outline: 'none', background: 'transparent',
          color: T.ink, fontFamily: A_FONT.sans, fontWeight: 500,
          fontSize: fs(T, 17), padding: 0, minWidth: 0, height: 44,
        }}/>
      {value && (
        <button onClick={onClear} aria-label="Clear search" style={{
          width: 44, height: 44, borderRadius: '50%',
          background: T.cardSoft, border: 'none', cursor: 'pointer',
          padding: 0, color: T.ink, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
      )}
      {/* Mic — stubbed. Visually present so the design carries; click is
          a no-op placeholder until HA Assist wiring lands. */}
      <button onClick={() => { /* TODO: HA Assist binding (polish PR) */ }}
        aria-label="Voice search (coming soon)" style={{
          width: 44, height: 44, borderRadius: '50%',
          background: 'transparent', border: 'none', cursor: 'pointer',
          padding: 0, color: T.mute, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="3" width="6" height="12" rx="3"/>
          <path d="M5 11a7 7 0 0 0 14 0M12 18v3"/>
        </svg>
      </button>
    </div>
  );
}

// TrackRow — used in S1 (search results > Tracks section) and S2
// (album/playlist track list). Cover + title + artist + duration +
// optional row index for ordered listings.
export function TrackRow({ T, track, onClick, showIndex = false, index = 0 }) {
  const artist = track.artists?.map((a) => a.name).join(', ') || '';
  const duration = fmtDuration(track.duration_ms);
  const images = track.album?.images || track.images || [];
  const imageUrl = pickSmallestAtLeast(images, 80);
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 14, width: '100%',
      padding: '10px 4px', minHeight: 64,
      border: 'none', borderBottom: `1px solid ${T.hair}`,
      background: 'transparent', cursor: 'pointer', textAlign: 'left',
      color: T.ink, fontFamily: A_FONT.sans,
    }}>
      {showIndex && (
        <span style={{
          width: 24, textAlign: 'right', flexShrink: 0,
          fontFamily: A_FONT.sans, fontSize: fs(T, 13), fontWeight: 500, color: T.mute,
          fontVariantNumeric: 'lining-nums tabular-nums',
        }}>{index + 1}</span>
      )}
      <div style={{
        width: 44, height: 44, borderRadius: 6, overflow: 'hidden',
        background: T.cardSoft, flexShrink: 0,
      }}>
        {imageUrl
          ? <img src={imageUrl} alt="" width={44} height={44}
                 style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}/>
          : <CoverArt seed={track.id || track.name} size={44} radius={6} label={false}/>}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500, color: T.ink,
          lineHeight: 1.25,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{track.name}</div>
        {artist && (
          <div style={{
            fontSize: fs(T, 13), color: T.inkSoft, marginTop: 2,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{artist}</div>
        )}
      </div>
      <span style={{
        fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.mute,
        flexShrink: 0, fontVariantNumeric: 'lining-nums tabular-nums',
      }}>{duration}</span>
    </button>
  );
}

function fmtDuration(ms) {
  if (!ms) return '';
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function pickSmallestAtLeast(images, minSize) {
  if (!Array.isArray(images) || images.length === 0) return null;
  const ascending = [...images].sort((a, b) => (a.width || 0) - (b.width || 0));
  return (ascending.find((img) => (img.width || 0) >= minSize) || ascending[ascending.length - 1])?.url || null;
}

// Scrubber — playback position control. Reads HA's media_position
// (position at media_position_updated_at), ticks forward locally
// between HA pushes (HA only re-emits position attributes on
// pause/resume/seek, not continuously), and seeks via the optional
// `onSeek(seconds)` on release.
//
// Returns null when duration is missing or zero — radio streams
// don't expose a duration so the scrubber would have nothing to
// represent. Hero handles the null cleanly.
export function Scrubber({ T, position, duration, updatedAt, playing, onSeek }) {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [playing]);

  const hasDuration = !!duration && duration > 0;

  // Live position: HA's last-known + delta from updatedAt. Clamped to
  // duration so the thumb never overshoots the end while we wait for
  // HA to push the next track's attributes.
  const live = React.useMemo(() => {
    const base = position || 0;
    if (!hasDuration) return base;
    if (!playing || !updatedAt) return Math.min(duration, base);
    const updatedMs = new Date(updatedAt).getTime();
    if (Number.isNaN(updatedMs)) return Math.min(duration, base);
    const delta = Math.max(0, (now - updatedMs) / 1000);
    return Math.min(duration, base + delta);
  }, [now, position, updatedAt, playing, duration, hasDuration]);

  // While the user is dragging, show the dragged value (so the thumb
  // doesn't snap back to the live position mid-drag). Cleared on commit.
  const [dragValue, setDragValue] = React.useState(null);
  const displayed = dragValue != null ? dragValue : live;

  if (!hasDuration) return null;

  return (
    <div style={{ width: '100%', maxWidth: 460 }}>
      <div style={{ padding: '0 2px' }}>
        <Slider
          value={Math.round(displayed)}
          min={0} max={Math.round(duration)} step={1}
          color={T.live} track={T.cardSoft}
          onChange={(v) => setDragValue(v)}
          onCommit={() => {
            if (dragValue != null) {
              onSeek?.(Math.round(dragValue));
              setDragValue(null);
            }
          }}/>
      </div>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        fontFamily: A_FONT.sans, fontSize: fs(T, 13), fontWeight: 500, color: T.mute,
        marginTop: -4, fontVariantNumeric: 'lining-nums tabular-nums', padding: '0 4px',
      }}>
        <span>{fmtScrubTime(displayed)}</span>
        <span>{fmtScrubTime(duration)}</span>
      </div>
    </div>
  );
}

function fmtScrubTime(seconds) {
  const total = Math.max(0, Math.round(seconds || 0));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// VolumeBar — single volume control: round − / + keys either side of
// the shared 44px Slider, level numeral on the right. No outline; the
// keys carry the quiet fill. `kind` drives the fill colour + indent:
//   - primary:   full width, ink fill
//   - extension: 18px indent under primary (used for pool-extension
//                secondary rocker, per §7 + §11.11 "bracketed under
//                primary"), ink fill
//   - member:    lighter inkSoft fill — used in S5 per-member rows
//
// Stateless / controlled. Parent owns the level and onChange wiring;
// the +/- buttons step by 2 to match the existing AMediaModal rocker.
export function VolumeBar({ T, kind = 'primary', label, level = 0, onChange, disabled = false }) {
  const indent = kind === 'extension' ? 18 : 0;
  const accentColor = kind === 'member' ? T.inkSoft : T.ink;

  // ── Intent layer (see volume-drag.js) ──────────────────────────────
  // `local` is non-null from the first pointer event until HA agrees
  // with what we last asked for (or we time out), and it is what the
  // thumb renders from. Without it the thumb renders from the echo,
  // which lags the finger, so the control snaps backwards mid-drag.
  // Sends are rate-limited to SEND_INTERVAL_MS because Slider fires
  // onChange on every pointermove and the group path fans out one call
  // per member — a five-speaker group was ~300 calls per second of drag.
  //
  // onChange is called as onChange(value, phase) where phase is
  // 'start' | 'move' | 'end'. Existing callers that ignore the second
  // argument keep working; the group handler needs it to capture a
  // per-gesture baseline (see groupTargets).
  const [local, setLocal] = React.useState(null);
  const localRef = React.useRef(null);
  const lastSentAt = React.useRef(null);
  const lastSentValue = React.useRef(null);
  const dragging = React.useRef(false);
  const flushTimer = React.useRef(null);
  const settleTimer = React.useRef(null);

  const shown = local ?? level;

  const clearLocal = React.useCallback(() => {
    clearTimeout(settleTimer.current);
    setLocal(null);
    localRef.current = null;
    lastSentAt.current = null;
    lastSentValue.current = null;
  }, []);

  // Drop the optimistic value as soon as the echo agrees. Only once the
  // finger is up — mid-drag we never defer to HA, whatever it says.
  React.useEffect(() => {
    if (local == null || dragging.current) return;
    if (isSettled(level, lastSentValue.current)) clearLocal();
  }, [level, local, clearLocal]);

  React.useEffect(() => () => {
    clearTimeout(flushTimer.current);
    clearTimeout(settleTimer.current);
  }, []);

  const send = (v, phase) => {
    lastSentAt.current = Date.now();
    lastSentValue.current = v;
    onChange?.(v, phase);
  };

  const handleMove = (v) => {
    if (disabled) return;
    const phase = dragging.current ? 'move' : 'start';
    dragging.current = true;
    setLocal(v);
    localRef.current = v;
    clearTimeout(flushTimer.current);
    if (shouldSend(Date.now(), lastSentAt.current)) {
      send(v, phase);
    } else {
      // Trailing edge — the last position inside a throttle window must
      // still go out, otherwise stopping the finger mid-window leaves
      // the speaker a step behind where the thumb ended up.
      const wait = SEND_INTERVAL_MS - (Date.now() - lastSentAt.current);
      flushTimer.current = setTimeout(() => send(localRef.current, 'move'), Math.max(0, wait));
    }
  };

  const handleCommit = () => {
    if (disabled || !dragging.current) return;
    dragging.current = false;
    clearTimeout(flushTimer.current);
    // Always send on release, even if it equals the last throttled
    // send: it is idempotent (group targets are absolute, not deltas)
    // and it is what tells the consumer the gesture is over.
    send(localRef.current, 'end');
    // Hard backstop — if the echo never arrives we would rather show
    // the truth than keep lying indefinitely.
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(clearLocal, SETTLE_TIMEOUT_MS);
  };

  // The +/- buttons run the same path so they get the same optimistic
  // treatment and the same baseline capture, rather than a second
  // code path that bypasses it.
  const nudge = (v) => { handleMove(v); handleCommit(); };

  return (
    <div style={{ marginLeft: indent, width: '100%', maxWidth: 460 }}>
      {label && (
        <ALabel T={T} style={{ marginBottom: 6 }}>{label}</ALabel>
      )}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        opacity: disabled ? 0.5 : 1,
      }}>
        <button onClick={() => !disabled && nudge(Math.max(0, shown - 2))}
          disabled={disabled} aria-label="Volume down"
          style={{
            width: 44, height: 44, borderRadius: '50%', border: 'none', padding: 0,
            background: T.cardSoft, color: T.ink, flexShrink: 0,
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>{I.minus}</button>
        <div style={{ flex: 1, minWidth: 0, padding: '0 4px' }}>
          <Slider value={shown} relative onChange={handleMove} onCommit={handleCommit}
            color={accentColor} track={T.cardSoft}/>
        </div>
        <button onClick={() => !disabled && nudge(Math.min(100, shown + 2))}
          disabled={disabled} aria-label="Volume up"
          style={{
            width: 44, height: 44, borderRadius: '50%', border: 'none', padding: 0,
            background: T.cardSoft, color: T.ink, flexShrink: 0,
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>{I.plus}</button>
        <span style={{
          fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500, color: T.ink,
          minWidth: 28, textAlign: 'right', flexShrink: 0,
          fontVariantNumeric: 'lining-nums tabular-nums',
        }}>{shown}</span>
      </div>
    </div>
  );
}

// Transport — shuffle / prev / play-pause / next / repeat row.
// Stateless / controlled. Parent owns the playback state and the
// callbacks; this component just renders the buttons. accent (centre)
// button is larger and takes the primary fill; outer buttons are quiet
// round keys, with shuffle/repeat taking the selected fill when on.
export function Transport({ T, isPlaying, shuffle = false, repeat = 'off', viewport,
                          onShuffle, onPrev, onPlayPause, onNext, onRepeat }) {
  const isPhone = viewport === 'phone';
  return (
    <div style={{
      display: 'flex', gap: isPhone ? 8 : 12, alignItems: 'center',
      justifyContent: isPhone ? 'center' : 'flex-start',
    }}>
      <XportBtn T={T} active={shuffle} onClick={onShuffle} ariaLabel="Shuffle">
        {I.shuffle}
      </XportBtn>
      <XportBtn T={T} onClick={onPrev} ariaLabel="Previous">{I.prev}</XportBtn>
      <XportBtn T={T} accent onClick={onPlayPause} ariaLabel={isPlaying ? 'Pause' : 'Play'}>
        {isPlaying ? I.pause : I.play}
      </XportBtn>
      <XportBtn T={T} onClick={onNext} ariaLabel="Next">{I.next}</XportBtn>
      <XportBtn T={T} active={repeat !== 'off'} onClick={onRepeat} ariaLabel={`Repeat ${repeat}`}>
        <RepeatGlyph mode={repeat}/>
      </XportBtn>
    </div>
  );
}

function XportBtn({ T, onClick, accent, active, ariaLabel, children }) {
  const size = accent ? 60 : 48;
  return (
    <button onClick={onClick} aria-label={ariaLabel} style={{
      width: size, height: size, borderRadius: '50%', border: 'none', padding: 0,
      background: accent ? T.primary : (active ? T.selected : T.cardSoft),
      color: accent ? T.primaryInk : (active ? T.selectedInk : T.ink),
      cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'background .15s, color .15s',
      flexShrink: 0,
    }}>{children}</button>
  );
}

function RepeatGlyph({ mode }) {
  // 'off' | 'one' | 'all'. 'one' overlays a small "1" badge.
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 2l4 4-4 4"/>
      <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
      <path d="M7 22l-4-4 4-4"/>
      <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
      {mode === 'one' && (
        <text x="12" y="15.5" fontSize="7" fontFamily="sans-serif" fontWeight="600"
              textAnchor="middle" fill="currentColor" stroke="none">1</text>
      )}
    </svg>
  );
}

// Small visual helpers. SonosDots = the two-dot mark from §11.11 used
// to flag Sonos group members vs amp-extension destinations. LockGlyph
// = the lock icon used on locked destinations (Kitchen when TV's on)
// and in the explainer card.
function SonosDots({ T }) {
  return (
    <span style={{ display: 'inline-flex', gap: 3, flexShrink: 0, color: T.mute }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }}/>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }}/>
    </span>
  );
}
function LockGlyph({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="11" width="14" height="10" rx="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );
}

// MemberRow — used inside Tune-balance disclosure in S5 group view.
// Sonos two-dot mark + speaker name + per-member VolumeBar +
// drop-from-group affordance. Coordinator row gets a "Coordinator"
// secondary line and *no* drop button — can't drop the coordinator
// without ungrouping the whole group (per §7 / §11.11 design pass).
// The row wraps on a phone so the volume control keeps a usable width
// on its own line rather than shrinking to a stub beside the name.
export function MemberRow({ T, name, isCoordinator, volume, onVolumeChange, onDrop }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', flexWrap: 'wrap',
      columnGap: 14, rowGap: 4,
      padding: '10px 0', borderBottom: `1px solid ${T.hair}`,
    }}>
      <div style={{ flex: '1 1 140px', minWidth: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
        <SonosDots T={T}/>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
            color: T.ink, lineHeight: 1.25,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{name}</div>
          {isCoordinator && (
            <div style={{
              fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.inkSoft, marginTop: 2,
            }}>
              Coordinator
            </div>
          )}
        </div>
      </div>
      <div style={{ flex: '1 1 300px', maxWidth: 420, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <VolumeBar T={T} kind="member" level={volume ?? 0} onChange={onVolumeChange}/>
        </div>
        {/* Holds the drop column on the coordinator row too, so every
            member's slider lines up. */}
        {!isCoordinator && onDrop ? (
          <button onClick={onDrop} aria-label={`Drop ${name} from group`}
            style={{
              width: 44, height: 44, borderRadius: '50%', padding: 0,
              border: 'none', background: T.cardSoft,
              color: T.ink, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        ) : <span aria-hidden style={{ width: 44, flexShrink: 0 }}/>}
      </div>
    </div>
  );
}

// DestChip — amp-zone destination chip (Kitchen / Dining / Pool /
// Rumpus). Per §6 these are NOT Sonos targets — they're routing
// affordances tied to the source room's amp zones. Visual treatment
// per the 2026-10 design: a quiet key that takes the selected fill
// while the zone is playing, name in the middle, optional lock glyph +
// "TV linked" on the right when locked.
export function DestChip({ T, name, active, locked, lockReason, onClick }) {
  return (
    <button
      disabled={locked && active}
      onClick={() => { if (!locked) onClick?.(); }}
      title={locked ? lockReason : undefined}
      style={{
        flex: '0 1 auto', minWidth: 110, maxWidth: 260,
        height: 48, padding: '0 18px',
        border: 'none',
        background: active ? T.selected : T.cardSoft,
        color: active ? T.selectedInk : T.ink,
        borderRadius: RADIUS.control,
        cursor: locked ? 'not-allowed' : 'pointer',
        opacity: locked && !active ? 0.5 : 1,
        fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        whiteSpace: 'nowrap',
        transition: 'background .18s ease-out, color .18s ease-out',
      }}>
      {name}
      {locked && (
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          color: 'inherit', opacity: 0.72,
          fontFamily: A_FONT.sans, fontSize: fs(T, 13), fontWeight: 500,
        }}>
          <LockGlyph size={13}/>
          TV linked
        </span>
      )}
    </button>
  );
}

// DestExplainer — persistent card below the chip rail when a chip is
// locked (e.g. Kitchen when Pavilion TV is on). Per §11.10: tooltip on
// long-press is fine for power users, but the explainer card carries
// the load-bearing plain-English message.
export function DestExplainer({ T, message }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: '14px 16px', marginTop: 12,
      background: T.cardSoft, borderRadius: RADIUS.control,
    }}>
      <span style={{ color: T.inkSoft, flexShrink: 0, marginTop: 2, display: 'flex' }}>
        <LockGlyph size={16}/>
      </span>
      <div style={{
        fontFamily: A_FONT.sans, fontSize: fs(T, 14), color: T.ink, lineHeight: 1.45,
      }}>
        {message}
      </div>
    </div>
  );
}

// SpeakerRow — one row per Sonos unit in S3's target picker.
//
// Visual: glyph (cover when playing, speaker icon otherwise) +
// display name + area subtitle on the left; state badge + chevron
// on the right. Disabled rows render with reduced opacity and a
// not-allowed cursor — used for TV-busy (Office Arc on TV source)
// and Offline (entity unavailable).
//
// `state` shape (computed by S3 from speakers+hass):
//   { kind: 'idle'|'playing'|'in-group'|'tv-busy'|'offline',
//     badge: string,            // human-readable
//     mediaTitle?: string,      // when playing solo
//     groupCoordName?: string,  // when in-group with someone else
//     batteryPct?: number }     // portables only
export function SpeakerRow({ T, name, areaLabel, state, onClick, selectable = false, selected = false }) {
  const disabled = state.kind === 'tv-busy' || state.kind === 'offline';
  return (
    <button onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-pressed={selectable ? selected : undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: 14, width: '100%',
        padding: '10px 8px', minHeight: 64,
        border: 'none', borderBottom: `1px solid ${T.hair}`,
        background: selected ? T.selectedSoft : 'transparent',
        cursor: disabled ? 'not-allowed' : 'pointer',
        textAlign: 'left',
        opacity: disabled ? 0.5 : 1,
        fontFamily: A_FONT.sans, color: T.ink,
        transition: 'background .12s',
      }}>
      <span style={{
        width: 40, height: 40, borderRadius: RADIUS.key,
        background: T.cardSoft,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: T.inkSoft, flexShrink: 0,
      }}>
        {I.speaker}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
          color: T.ink, lineHeight: 1.25,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{name}</div>
        {areaLabel && (
          <div style={{ fontSize: fs(T, 13), color: T.inkSoft, marginTop: 2 }}>
            {areaLabel}
          </div>
        )}
      </div>
      <div style={{
        fontFamily: A_FONT.sans, fontSize: fs(T, 13),
        fontWeight: state.kind === 'playing' ? 500 : 400,
        color: state.kind === 'playing' ? T.live : T.inkSoft,
        textAlign: 'right', flexShrink: 1, minWidth: 0, marginRight: 4,
        maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>{state.badge}</div>
      {/* Tail glyph: checkbox (selectable mode) or chevron (default
          dispatch mode). Selectable rows swap between an empty quiet
          square and a filled selected-coloured check so the
          multi-select state is unambiguous against the row-tint
          highlight. */}
      {!disabled && selectable && (
        <span style={{
          width: 26, height: 26, borderRadius: 7,
          background: selected ? T.selected : T.keyFill,
          boxShadow: selected ? 'none' : T.keyShadow,
          color: selected ? T.selectedInk : T.mute,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          transition: 'background .12s',
        }}>
          {selected && (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12l5 5L20 7"/>
            </svg>
          )}
        </span>
      )}
      {!disabled && !selectable && (
        <span style={{ color: T.mute, display: 'flex', flexShrink: 0 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 6l6 6-6 6"/>
          </svg>
        </span>
      )}
    </button>
  );
}

// PresetTile — named-group preset row in S3.
//
// Visually heavier than SpeakerRow (named groups carry more semantic
// weight): a card surface rather than a list row. Two-line layout:
// title + secondary member summary. Tap → onClick(presetKey).
// "Currently playing" gets a "Join in" badge when active.
export function PresetTile({ T, title, members, badge, onClick }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 14, width: '100%',
      padding: '16px 18px', minHeight: 68,
      border: 'none', borderRadius: RADIUS.card, boxShadow: T.cardShadow,
      background: T.card, cursor: 'pointer', textAlign: 'left',
      fontFamily: A_FONT.sans, color: T.ink,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: A_FONT.sans, fontSize: fs(T, 16), fontWeight: 500,
          color: T.ink, lineHeight: 1.25,
        }}>{title}</div>
        {members && (
          <div style={{ fontSize: fs(T, 13), color: T.inkSoft, marginTop: 3 }}>
            {members}
          </div>
        )}
      </div>
      {badge && (
        <span style={{
          fontFamily: A_FONT.sans, fontSize: fs(T, 13), fontWeight: 500, color: T.live,
          padding: '5px 12px', borderRadius: RADIUS.key,
          background: T.cardSoft,
          flexShrink: 0, whiteSpace: 'nowrap',
        }}>{badge}</span>
      )}
      <span style={{ color: T.mute, display: 'flex', flexShrink: 0 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 6l6 6-6 6"/>
        </svg>
      </span>
    </button>
  );
}

// ContentTile — square cover + serif italic title + sans subtitle.
// Cover prefers an explicit imageUrl; falls back to the deterministic
// CoverArt gradient when no URL is available. Tap → onClick.
//
// Image-loading strategy per §11.5 + Sonos favourites observation:
//   - HA-relative URLs (start with `/`, e.g. /api/media_player_proxy/…)
//     need same-origin credentials to authenticate against HA — route
//     them through useImageBlobUrl which does the auth fetch + blob.
//     This is the path Sonos favourites typically take.
//   - Absolute URLs (Spotify CDN at https://i.scdn.co/, etc.) are
//     public and load via direct <img src>. Faster, no auth dance.
export function ContentTile({ T, title, subtitle, imageUrl, seed, onClick, size = 120 }) {
  // Sonos sometimes returns HTTP thumbnail URLs even though the panel
  // runs on HTTPS (Spotify static-resources S3, legacy CloudFront,
  // etc.) — the browser blocks those as Mixed Content. Upgrade
  // http→https in-place; if the host doesn't serve HTTPS, onError
  // below falls back to CoverArt gradient.
  const httpsUrl = (imageUrl && imageUrl.startsWith('http://'))
    ? 'https://' + imageUrl.slice(7)
    : imageUrl;
  // Known-dead Spotify "no artwork" placeholder URLs — both 404 (the
  // S3 bucket is stale, the pl.scdn.co/default route returns nothing).
  // Sonos returns these in browse_media for playlists that have no
  // custom artwork in Spotify. Skip the network request entirely and
  // render CoverArt gradient straight away — no broken-image flash.
  const isDeadPlaceholder = !!httpsUrl && (
    httpsUrl.includes('spotify-static-resources.s3.amazonaws.com/img/playlist_legacy') ||
    httpsUrl.includes('pl.scdn.co/images/pl/default/')
  );
  const isHaProxy = !!httpsUrl && httpsUrl.startsWith('/') && !isDeadPlaceholder;
  const blobUrl = useImageBlobUrl(isHaProxy ? httpsUrl : null);
  const displayUrl = isDeadPlaceholder ? null : (isHaProxy ? blobUrl : httpsUrl);
  // Some thumbnails (TuneIn radio station logos, custom Sonos
  // playlists) 404 even when not on our known-dead list — show the
  // CoverArt gradient instead of a broken-image icon. Reset on URL
  // change so a retry on a different tile doesn't inherit the prior
  // failure.
  const [imgFailed, setImgFailed] = React.useState(false);
  React.useEffect(() => { setImgFailed(false); }, [displayUrl]);
  const showImage = !!displayUrl && !imgFailed;
  return (
    <button onClick={onClick} style={{
      display: 'flex', flexDirection: 'column', gap: 8,
      width: size, padding: 0, border: 'none', background: 'transparent',
      cursor: 'pointer', textAlign: 'left',
      fontFamily: A_FONT.sans, color: T.ink,
    }}>
      <div style={{
        width: size, height: size, borderRadius: RADIUS.key, overflow: 'hidden',
        background: T.cardSoft, boxShadow: T.cardShadow,
      }}>
        {showImage
          ? <img src={displayUrl} alt="" width={size} height={size}
                 onError={() => {
                   console.warn(`[music] thumbnail failed for "${title}":`, displayUrl);
                   setImgFailed(true);
                 }}
                 style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}/>
          : <CoverArt seed={seed || title || 'untitled'} size={size} radius={RADIUS.key} label={false}/>}
      </div>
      <div style={{ minWidth: 0, width: '100%' }}>
        <div style={{
          fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
          color: T.ink, lineHeight: 1.25,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{title || ''}</div>
        {subtitle && (
          <div style={{
            fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.inkSoft,
            marginTop: 2,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{subtitle}</div>
        )}
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// PR 7 — Skeleton placeholders + integration-error banner.
//
// Used in S0 / S1 while Spotify endpoints are in flight, and in place
// of the inline grey error text the earlier PRs shipped. Skeletons
// reuse the `a-pulse` keyframe defined in direction-a.jsx's global
// style block so we don't add a second animation primitive.
// ─────────────────────────────────────────────────────────────────────

export function Skeleton({ T, width, height, radius = 6, style }) {
  return (
    <div style={{
      width, height, borderRadius: radius,
      background: T.cardSoft,
      animation: 'a-pulse 1.6s ease-in-out infinite',
      ...style,
    }}/>
  );
}

// SkeletonTile — placeholder shaped like a ContentTile (square cover +
// two text lines). Occupies the same vertical space the real tile
// will so the layout doesn't jump when data arrives.
export function SkeletonTile({ T, size = 120 }) {
  return (
    <div style={{ width: size, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Skeleton T={T} width={size} height={size} radius={RADIUS.key}/>
      <Skeleton T={T} width={Math.round(size * 0.85)} height={13} radius={4}
        style={{ marginTop: 4 }}/>
      <Skeleton T={T} width={Math.round(size * 0.55)} height={11} radius={4}/>
    </div>
  );
}

// SkeletonCarousel — row of skeleton tiles. No scroll snap (no point
// when the content is placeholder), no interaction. Default 6 tiles
// keeps the visible viewport roughly populated at the panel's main
// 1024px form factor.
export function SkeletonCarousel({ T, count = 6, size = 120 }) {
  return (
    <div style={{ display: 'flex', gap: 12, overflow: 'hidden' }}>
      {Array.from({ length: count }, (_, i) => (
        <SkeletonTile key={i} T={T} size={size}/>
      ))}
    </div>
  );
}

// SkeletonRow — placeholder for a single TrackRow (44px cover + two
// text lines). Used in S1's "Tracks" section during search.
export function SkeletonRow({ T }) {
  return (
    <div style={{
      display: 'flex', gap: 14, alignItems: 'center',
      padding: '10px 4px', minHeight: 64, boxSizing: 'border-box',
      borderBottom: `1px solid ${T.hair}`,
    }}>
      <Skeleton T={T} width={44} height={44} radius={6}/>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <Skeleton T={T} width="60%" height={13} radius={4}/>
        <Skeleton T={T} width="40%" height={11} radius={4}/>
      </div>
    </div>
  );
}

// IntegrationErrorBanner — replaces the inline grey error text the
// earlier music PRs used. A card with the attention-coloured glyph so
// it reads as "something needs attention" without shouting. Title is
// the human-readable summary; `message` is the raw upstream error
// (truncated by the caller). Retry button when a retry callback is
// provided.
export function IntegrationErrorBanner({ T, title = 'Something went wrong', message, onDismiss, onRetry }) {
  return (
    <div role="alert" style={{
      display: 'flex', alignItems: 'flex-start', gap: 14,
      padding: '18px 20px',
      background: T.card, borderRadius: RADIUS.card, boxShadow: T.cardShadow,
      animation: 'a-pop .18s ease',
    }}>
      <div style={{ color: T.attention, marginTop: 1, flexShrink: 0, display: 'flex' }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="1.7"
             strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M12 8v4M12 16h.01"/>
        </svg>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: A_FONT.sans, fontWeight: 500,
          fontSize: fs(T, 15), color: T.ink, lineHeight: 1.3,
        }}>{title}</div>
        {message && (
          <div style={{
            fontFamily: A_FONT.sans, fontSize: fs(T, 13),
            color: T.inkSoft, marginTop: 4, lineHeight: 1.45,
            wordBreak: 'break-word',
          }}>{message}</div>
        )}
        {(onRetry || onDismiss) && (
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            {onRetry && (
              <button onClick={onRetry} style={{
                height: 44, padding: '0 20px', borderRadius: RADIUS.control,
                border: 'none', background: T.primary, color: T.primaryInk,
                cursor: 'pointer',
                fontFamily: A_FONT.sans, fontWeight: 500,
                fontSize: fs(T, 15),
              }}>Try again</button>
            )}
            {onDismiss && (
              <button onClick={onDismiss} style={{
                height: 44, padding: '0 20px', borderRadius: RADIUS.control,
                border: 'none', background: T.cardSoft,
                color: T.ink, cursor: 'pointer',
                fontFamily: A_FONT.sans, fontWeight: 500, fontSize: fs(T, 15),
              }}>Dismiss</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
